import React, { forwardRef } from 'react';

import type { InputNumberRef } from '@rc-component/input-number';
import { InputNumber } from 'antd';
import EditableCell from 'components/common/EditableCell';
import MergeClassNames from 'utils/MergeClassNames';

export interface NumericInputFieldProps {
  /** Label shown to the left of the input. Omit in table-cell contexts where the column header serves as label. */
  label?: string;
  /** Extra className forwarded to the outer wrapper <div> (controls width, height, etc.). */
  className?: string;
  value: number | null;
  /** Grayed-out hint when value is null (onCommit / EditableCell mode only). */
  placeholder?: string;
  /**
   * Unit suffix rendered as a <span> to the right of the input, e.g. "mm" or "L/ha".
   * Styled via unitClassName (defaults to plain gray text). Omit when no unit is needed.
   */
  unit?: string;
  /**
   * className for the unit <span>. Defaults to 'flex-shrink-0 text-sm text-gray-muted'
   * (plain gray label). Pass a custom class to change the appearance, e.g. to add a
   * border that visually attaches the unit to the input box.
   */
  unitClassName?: string;
  min?: number;
  max?: number;
  inclusiveMin?: boolean;
  /** Allow the field to be saved as null (empty). onCommit mode only. */
  allowEmpty?: boolean;
  /**
   * Custom display node shown in click-to-edit view mode (onCommit mode only).
   * Use when the display format differs from the raw number, e.g. formatCurrency(cost).
   * When omitted, falls back to value (or placeholder when value is null).
   */
  displayValue?: React.ReactNode;
  /** Extra className forwarded to the underlying input (controls width, bg, etc.). */
  inputClassName?: string;
  disabled?: boolean;
  /** Decimal places to round to (onChange mode only). */
  precision?: number;
  /** Keyboard increment step (onChange mode only). */
  step?: number;
  /** Show/hide the +/- spinner buttons (onChange mode only). */
  controls?: boolean;
  /** Set false to stop Arrow Up/Down stepping the value (onChange mode only). */
  keyboard?: boolean;
  /** Validation state passed to InputNumber (onChange mode only). */
  status?: 'error' | 'warning';
  /** Size of the InputNumber control (onChange mode only). Defaults to 'small' for table contexts; pass 'middle' in form contexts to match Select/DatePicker. */
  size?: 'large' | 'middle' | 'small';
  /** id forwarded to the inner input element — use when an external <label htmlFor> links to this field (onChange mode only). */
  id?: string;
  /**
   * Additional onKeyDown handler (onChange mode only). The component's own
   * decimal-restriction logic runs first; this handler is then called for any
   * extra behaviour (e.g. arrow-key row navigation).
   */
  onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>;
  /** Handles Arrow Up/Down while editing, e.g. to move to the adjacent row (onCommit mode only). */
  onArrowNavigate?: (direction: 'up' | 'down', from: HTMLElement) => void;
  /** Called on blur (onChange mode only). Separate from onCommit — use for pure UI side-effects such as marking a field as touched. */
  onBlur?: () => void;
  /**
   * Fires on every keystroke. When provided, renders an always-visible
   * InputNumber; the component ref is forwarded so callers can call .focus().
   */
  onChange?: (value: number | null) => void;
  /**
   * Fires on blur / Enter. When onChange is absent, renders a click-to-edit
   * EditableCell. Required when onChange is not provided.
   */
  onCommit?: (value: number | null) => void;
}

/**
 * Inline row: optional label — numeric input — optional unit suffix.
 *
 * Two render modes, selected by which callback is provided:
 *   - onChange  → always-visible AntD InputNumber (ref-forwardable; supports
 *                 precision / step / onKeyDown)
 *   - onCommit  → click-to-edit EditableCell that commits on blur or Enter
 *
 * In onChange mode the component restricts keyboard input to digits and a
 * single decimal point, mirroring the mobile handleDecimalInput behaviour and
 * the existing EditableCell restriction.
 */
const NumericInputField = forwardRef<InputNumberRef, NumericInputFieldProps>(
  (
    {
      label,
      className,
      value,
      placeholder,
      displayValue: displayValueProp,
      unit,
      unitClassName,
      min,
      max,
      inclusiveMin,
      allowEmpty,
      inputClassName,
      disabled,
      precision,
      step,
      controls,
      keyboard,
      status,
      id,
      onKeyDown,
      onArrowNavigate,
      onBlur,
      onChange,
      onCommit,
      size = 'small',
    },
    ref
  ) => {
    // In onCommit (click-to-edit) mode: explicit displayValue prop wins; fall
    // back to placeholder styling when value is null, then raw value.
    const displayValue =
      displayValueProp !== undefined ? (
        displayValueProp
      ) : value === null && placeholder ? (
        <span className="text-gray-muted">{placeholder}</span>
      ) : (
        value
      );

    // Restrict onChange-mode input to digits and a single decimal point,
    // then forward the caller's own handler (e.g. arrow-key row navigation).
    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (
        event.key.length === 1 &&
        !event.ctrlKey &&
        !event.metaKey &&
        /[^0-9.]/.test(event.key)
      ) {
        event.preventDefault();
      }
      if (
        event.key === '.' &&
        (event.target as HTMLInputElement).value.includes('.')
      ) {
        event.preventDefault();
      }
      onKeyDown?.(event);
    };

    return (
      <div className={MergeClassNames('flex items-center gap-2', className)}>
        {label && (
          <span className="w-20 flex-shrink-0 whitespace-nowrap text-sm font-semibold text-gray-dark">
            {label}
          </span>
        )}
        {onChange ? (
          <InputNumber
            ref={ref}
            id={id}
            size={size}
            min={min}
            max={max}
            controls={controls}
            keyboard={keyboard}
            precision={precision}
            step={step}
            disabled={disabled}
            status={status}
            className={inputClassName}
            value={value}
            placeholder={placeholder}
            onChange={onChange}
            onKeyDown={handleKeyDown}
            onBlur={onBlur}
          />
        ) : (
          <EditableCell
            type="number"
            min={min}
            inclusiveMin={inclusiveMin}
            allowEmpty={allowEmpty}
            disabled={disabled}
            value={value}
            displayValue={displayValue}
            className={inputClassName}
            onArrowNavigate={onArrowNavigate}
            onCommit={onCommit!}
          />
        )}
        {unit && (
          <span
            className={unitClassName ?? 'flex-shrink-0 text-sm text-gray-muted'}
          >
            {unit}
          </span>
        )}
      </div>
    );
  }
);

NumericInputField.displayName = 'NumericInputField';

export default NumericInputField;
